/**
 * The member-facing circle identifier (docs/architecture.md §6): `<slug>-<key>`, e.g.
 * `family-7k2x`. The key is permanent and random; the slug part is cosmetic, so a
 * renamed circle keeps its links. Owners keep addressing circles by slug (unique per
 * account); members only ever see the identifier.
 */

/** Lowercase letters and digits without the ambiguous 0/1/i/l/o. */
export const CIRCLE_KEY_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
export const CIRCLE_KEY_LENGTH = 4;

const KEY_RE = new RegExp(`^[${CIRCLE_KEY_ALPHABET}]{${CIRCLE_KEY_LENGTH}}$`);

export function isValidCircleKey(key: string): boolean {
  return KEY_RE.test(key);
}

/** A random key from Web Crypto, unbiased (bytes past the last full alphabet cycle are redrawn). */
export function makeCircleKey(): string {
  const n = CIRCLE_KEY_ALPHABET.length;
  const limit = 256 - (256 % n);
  let key = '';
  while (key.length < CIRCLE_KEY_LENGTH) {
    const bytes = crypto.getRandomValues(new Uint8Array(CIRCLE_KEY_LENGTH * 2));
    for (const b of bytes) {
      if (b < limit && key.length < CIRCLE_KEY_LENGTH) key += CIRCLE_KEY_ALPHABET[b % n];
    }
  }
  return key;
}

/** `family` + `7k2x` → `family-7k2x`. A circle without a key (created before keys) is its bare slug. */
export function formatCircleId(slug: string, key?: string | null): string {
  return key ? `${slug}-${key}` : slug;
}

/**
 * `family-7k2x` → `{ slug: 'family', key: '7k2x' }`: the key is the part after the last `-`.
 * Null when there is no well-formed key suffix (a legacy bare slug such as `family`).
 */
export function parseCircleId(id: string): { slug: string; key: string } | null {
  const dash = id.lastIndexOf('-');
  if (dash <= 0) return null;
  const key = id.slice(dash + 1);
  const slug = id.slice(0, dash);
  if (!isValidCircleKey(key) || slug.endsWith('-')) return null;
  return { slug, key };
}

/** Whether a circle (`{ slug, key? }`) is the one `id` from a URL names: by key, else by legacy bare slug. */
export function circleMatchesId(circle: { slug: string; key?: string | null }, id: string): boolean {
  const parsed = parseCircleId(id);
  if (parsed && circle.key && parsed.key === circle.key) return true;
  return circle.slug === id;
}

/**
 * Which stored entry (the launcher keys device tokens by circle identifier, older
 * launchers by bare slug) belongs to the circle `id` from a URL names. Exact match
 * first, then the same key under another slug (renamed circle), then a legacy entry
 * keyed by the slug part, then an entry whose slug part is the legacy bare `id`.
 */
export function findCircleEntryKey(keys: Iterable<string>, id: string): string | null {
  const all = [...keys];
  if (all.includes(id)) return id;
  const parsed = parseCircleId(id);
  if (parsed) {
    const sameKey = all.find((k) => parseCircleId(k)?.key === parsed.key);
    if (sameKey !== undefined) return sameKey;
    if (all.includes(parsed.slug)) return parsed.slug;
    return null;
  }
  return all.find((k) => parseCircleId(k)?.slug === id) ?? null;
}
